import { updateNodeDescription } from '@ui/lib/reactFlow/helpers'
import { useEffect, useMemo, useState } from 'react'
import { IoCheckmark, IoClose } from 'react-icons/io5'
import { Node, NodeProps } from 'reactflow'
import { useShallow } from 'zustand/react/shallow'

import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { NodeStep } from '@ui/__generated__/graphql'
import { Chip } from '@ui/components/core'
import { CloseIcon, PlusIcon } from '@ui/components/icons'
import { MAX_TRANSFORMATION_STEPS } from '@ui/constants/pipelinesWorkflow'
import { convertBytesToText } from '@ui/lib/utils'
import { useLayoutPanelManagerStore, useNodeAndEdgeStore, useSaveUpdateStatusStore } from '@ui/stores/pipelinesWorkflow'
import { Config } from '@ui/stores/pipelinesWorkflow/NodeStepStore'
import { useQueryBuilderStore } from '@ui/stores/pipelinesWorkflow/QueryBuilderStore'
import clsx from 'clsx'

import { LayoutPanel, RIGHT_PANEL_CONTENT } from '../PipelineLayoutManager'
import { CreateStep } from './CreateStep'
import { SortableStep } from './Step'

export const TransformationSteps = ({ node }: { node: NodeProps }) => {
  const [activeId, setActiveId] = useState<string | null>(null)
  const [openStepIds, setOpenStepIds] = useState<string[]>([])
  const [openStepMenu, setOpenStepMenu] = useState<string | null>(null)
  const [steps, setSteps] = useState<NodeStep[]>(node.data.steps)
  const [description, setDescription] = useState<string>(node.data.description)
  const [showDescriptionInput, setShowDescriptionInput] = useState<boolean>(false)
  const [showMoreDescription, setShowMoreDescription] = useState<boolean>(false)

  const { queryConfig, setIds, inputId, showOverlay, setShowOverlay } = useQueryBuilderStore(
    useShallow((state) => ({
      queryConfig: state.queryConfig,
      setIds: state.setIds,
      inputId: state.inputId,
      showOverlay: state.showOverlay,
      setShowOverlay: state.setShowOverlay,
    }))
  )

  const { setModalContent, setPanelContent } = useLayoutPanelManagerStore(
    useShallow((state) => ({
      setModalContent: state.setModalContent,
      setPanelContent: state.setPanelContent,
    }))
  )

  const { setNodes, setRefreshLayout, updateNodeStepConfig } = useNodeAndEdgeStore(
    useShallow((state) => ({
      setNodes: state.setNodes,
      setRefreshLayout: state.setRefreshLayout,
      updateNodeStepConfig: state.updateNodeStepConfig,
    }))
  )

  const setPipelineModified = useSaveUpdateStatusStore(useShallow((state) => state.setPipelineModified))

  useEffect(() => {
    setSteps(node.data.steps)
  }, [node.data.steps])

  // Reset the overlay when the current node changes
  useEffect(() => {
    setShowOverlay(false)
  }, [])

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const outputSize = useMemo(() => {
    const size = node.data.size
    return convertBytesToText(size.log + size.span + size.metric)
  }, [node.data.size])

  const openAddStep = () => {
    setModalContent({ content: <CreateStep node={node} onAddStep={(step) => toggleStep(step.id)} />, open: true })
  }

  const openQueryBuilder = (stepId: string, inputId: string) => {
    setPanelContent({ panel: LayoutPanel.INNER_RIGHT_PANEL, content: RIGHT_PANEL_CONTENT.QUERY_BUILDER, open: true })
    setIds(stepId, inputId)
  }

  const openCodeEditor = () => {
    setPanelContent({
      panel: LayoutPanel.INNER_RIGHT_PANEL,
      content: RIGHT_PANEL_CONTENT.TRANSFORMATION_CODE,
      open: true,
      ignoreMaxConstraints: true,
    })
  }

  const closePanel = () => {
    setPanelContent({ panel: LayoutPanel.RIGHT_PANEL, content: null, open: false })
  }

  const closeQueryBuilder = () => {
    handleSetSelectedStepOverlay(false)
    setPanelContent({ panel: LayoutPanel.INNER_RIGHT_PANEL, content: null, open: false })
    setIds('', '')
  }

  // This handles the side menu for options of a step
  const toggleStepMenu = (step: NodeStep) => {
    setOpenStepMenu((prevOpenStepMenu) => {
      if (prevOpenStepMenu === null && step.id) {
        return step.id
      }
      if (prevOpenStepMenu === step.id) {
        return null
      }
      return step.id || ''
    })
  }

  // Open/Close a step itself
  const toggleStep = (id: string) => {
    setOpenStepIds((prevOpenStepId) => {
      if (prevOpenStepId === null) {
        return [id]
      }
      if (prevOpenStepId.includes(id)) {
        return prevOpenStepId.filter((stepId) => stepId !== id)
      }
      return [...prevOpenStepId, id]
    })
  }

  const deleteStepFromNode = (step: NodeStep) => {
    const newSteps = node.data.steps
      .filter((s: NodeStep) => s.id !== step.id)
      .map((s: NodeStep, index: number) => ({ ...s, order: index }))
    setNodes((nodes) => nodes.map((n) => (n.id === node.id ? { ...n, data: { ...n.data, steps: newSteps } } : n)))
    setRefreshLayout(true)
    setPipelineModified(true)
  }

  const handleUpdateStepConfig = (stepId: string, config: Partial<Config>) => {
    // Remove the jsonata key if it exists
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { jsonata, ...rest } = config as Partial<Config & { jsonata?: unknown }>
    // Apply changes to the node step to the node in the pipeline
    updateNodeStepConfig(stepId, rest)
    setPipelineModified(true)
  }

  // Populate the query builder with the initial query
  // const handleSetInitialQuery = (step: NodeStep, inputId: string) => {
  //   const predicate = step.predicate as NodeStepPredicate | undefined
  //   setIds(step.id as string, inputId)
  //   if (predicate?.ruleGroup) updateQuery(predicate.ruleGroup)
  // }

  const handleSetSelectedStepOverlay = (show?: boolean) => {
    setShowOverlay(show ?? !showOverlay)
  }

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event
    setActiveId(active.id as string)
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (active.id !== over?.id) {
      setSteps((steps) => {
        const oldIndex = steps.findIndex((s: NodeStep) => s.id === active.id)
        const newIndex = steps.findIndex((s: NodeStep) => s.id === over?.id)
        const newSteps = arrayMove(steps, oldIndex, newIndex) as NodeStep[]
        const newStepOrder = newSteps.map((s: NodeStep, index: number) => ({ ...s, order: index }))
        setNodes((nodes) =>
          nodes.map((n) => {
            if (n.id === node.id) {
              return { ...n, data: { ...n.data, steps: newStepOrder } }
            }
            return n
          })
        )
        setRefreshLayout(true)
        setPipelineModified(true)
        return newStepOrder
      })
    }
  }

  // Shared props for SortableStep
  const sharedProps = {
    openStepIds,
    openStepMenu,
    toggleStep,
    toggleStepMenu,
    deleteStepFromNode,
    queryConfig,
    openQueryBuilder,
    openCodeEditor,
    handleUpdateStepConfig,
    handleSetSelectedStepOverlay,
  }

  return (
    <div className="relative flex h-full flex-col">
      {showOverlay && <div onClick={closeQueryBuilder} className="absolute inset-0 z-10 cursor-pointer bg-black/50" />}
      <div className="flex flex-col space-y-4 p-4">
        {/* Header */}
        <div className="flex w-full justify-between">
          <div className="flex flex-row items-center space-x-1">
            <p className="text-sm font-semibold">Transformation</p>

            {/* Data In/Out */}
            <div className="flex flex-row items-center space-x-1">
              <p className="text-sm font-medium">|</p>
              <p className="text-xs font-medium text-gray-900/70">{outputSize}/min out</p>
            </div>
          </div>

          <CloseIcon
            className="size-3 cursor-pointer stroke-gray-300 transition-all duration-300 ease-in-out hover:scale-110 hover:stroke-gray-500"
            onClick={closePanel}
          />
        </div>

        {/* Transformation Datatypes */}
        <div className="flex w-full flex-row space-x-1">
          <Chip>
            <div className="size-1.5 rounded-full bg-logsColor" />
            <p className="text-xs font-medium">Logs</p>
          </Chip>
          <Chip>
            <div className="size-1.5 rounded-full bg-spansColor" />
            <p className="text-xs font-medium">Spans</p>
          </Chip>
          {/* <Chip>
            <p className="text-xs font-medium">Metrics</p>
          </Chip> */}
        </div>

        {/* Description */}
        <div className="flex flex-col space-y-2">
          <div className={clsx('flex flex-row space-x-2 overflow-hidden', showMoreDescription ? 'h-auto' : 'h-4')}>
            {showDescriptionInput ? (
              <div className="relative h-auto w-full">
                <textarea
                  className="min-h-20 w-full resize-none rounded-md border border-gray-300 bg-white p-1 pb-6 text-xs font-normal focus:outline-0"
                  value={description}
                  maxLength={255}
                  onChange={(e) => setDescription(e.target.value)}
                />
                <p className="absolute bottom-2 right-3 rounded-md bg-white px-1 text-2xs text-gray-900">
                  {description.length}/255
                </p>
              </div>
            ) : (
              <p className={clsx('text-xs font-normal', showMoreDescription ? 'h-auto' : 'truncate')}>
                {node.data.description || 'No description'}
              </p>
            )}
          </div>

          <div className="flex w-full flex-row justify-between">
            <div className="flex flex-row space-x-2">
              <p
                onClick={() => setShowMoreDescription(showDescriptionInput ? true : !showMoreDescription)}
                className="cursor-pointer text-xs font-normal text-blue-400 transition-all duration-300 ease-in-out hover:text-blue-500">
                {showMoreDescription ? 'Show less' : 'Show more'}
              </p>
              <p
                onClick={() => {
                  setShowMoreDescription(true)
                  setShowDescriptionInput(!showDescriptionInput)
                  if (!showDescriptionInput) {
                    setDescription(node.data.description)
                  }
                }}
                className="cursor-pointer text-xs font-normal text-blue-400 transition-all duration-300 ease-in-out hover:text-blue-500">
                Edit
              </p>
            </div>

            {showDescriptionInput && (
              <div className="relative right-0 flex flex-row space-x-1">
                <div
                  onClick={() => {
                    setShowDescriptionInput(false)
                    setShowMoreDescription(false)
                    updateNodeDescription(node as unknown as Node, description)
                    setDescription(description)
                  }}
                  className="flex size-5 cursor-pointer items-center justify-center rounded bg-success-100 transition-all duration-300 ease-in-out hover:bg-success-50">
                  <IoCheckmark className="size-4 stroke-success-500 transition-all duration-300 ease-in-out hover:stroke-success-600" />
                </div>
                <div
                  onClick={() => {
                    setShowMoreDescription(false)
                    setShowDescriptionInput(false)
                    setDescription(node.data.description)
                  }}
                  className="flex size-5 cursor-pointer items-center justify-center rounded bg-negative-100 transition-all duration-300 ease-in-out hover:bg-negative-50">
                  <IoClose className="size-4 text-negative-500 transition-all duration-300 ease-in-out hover:text-negative-600" />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="h-px w-full bg-gray-200" />

      {/* Steps */}
      <div className="flex flex-col space-y-4 p-4">
        {/* Header */}
        <div className="flex h-7 w-full justify-between">
          <div className="flex flex-row space-x-1">
            <p className="text-xs font-semibold">Steps</p>
            <p className="text-xs font-semibold">
              ({steps.length}/{MAX_TRANSFORMATION_STEPS})
            </p>
          </div>

          {/* Add Step */}
          {steps.length < MAX_TRANSFORMATION_STEPS ? (
            <div
              className="flex size-6 cursor-pointer items-center justify-center rounded transition-all duration-300 ease-in-out hover:bg-gray-200"
              onClick={() => openAddStep()}>
              <PlusIcon className="size-4 stroke-gray-900" />
            </div>
          ) : null}
        </div>
      </div>

      {/* Steps List */}
      <div className="relative flex flex-col space-y-2">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}>
          <SortableContext items={steps} strategy={verticalListSortingStrategy}>
            {steps.map((step: NodeStep, index: number) => {
              return (
                <SortableStep
                  inputId={inputId}
                  key={index}
                  isDragging={false}
                  step={step}
                  index={index}
                  {...sharedProps}
                />
              )
            })}
          </SortableContext>
          <DragOverlay>
            {/* We  use just a placeholder of the selected step to drag */}
            {activeId ? (
              <SortableStep
                inputId={inputId}
                key={activeId}
                isDragging={true}
                step={steps.find((s) => s.id === activeId) as NodeStep}
                index={steps.findIndex((s) => s.id === activeId)}
                {...sharedProps}
              />
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>
    </div>
  )
}
